/**
 * License expiry service. Single source of truth for parsing `tokef_dt` and
 * computing the red/orange/green status that drives the banner UI.
 *
 * Replaces:
 *   - `license-banner.js::parseTokefToDate`
 *   - `license-banner.js::computeLicenseStatus`
 *   - `license-banner.js::daysDiff`
 *   - `license-banner.js::readTokefFromDB`
 *   - `alert-status.js::parseTokefDate`
 */

import { ref, readOnce } from '../../core/firebase.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { fetchOwnershipHistory } from '../../data/apis/ckan.js';
import { Ckan, Auth } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';
import { ensureGovBundle } from './ownership.service.js';

const log = createLogger('license');

/**
 * @typedef {'green' | 'orange' | 'red' | 'unknown'} LicenseStatus
 *
 * @typedef {Object} LicenseInfo
 * @property {LicenseStatus} status
 * @property {Date | null} tokefDate    Expiry date as native Date (UTC).
 * @property {number | null} daysLeft   Days from now until expiry (can be negative).
 * @property {string | null} prettyDate `DD/MM/YYYY` formatted for display.
 */

/** Threshold below which the status is "orange" (warning). */
const WARN_DAYS = 30;

/**
 * Parse a `tokef_dt` value into a Date. Accepts numbers (YYYYMMDD), strings,
 * and ISO. Returns null when unparseable.
 *
 * @param {unknown} value
 * @returns {Date | null}
 */
export function parseTokef(value) {
  if (value == null) return null;
  const s = String(value).trim().replace(/^\+/, '');
  if (!s) return null;
  if (/^\d{8}$/.test(s)) {
    const y = +s.slice(0, 4), m = +s.slice(4, 6), d = +s.slice(6, 8);
    return new Date(Date.UTC(y, m - 1, d));
  }
  if (/^\d{6}$/.test(s)) {
    const y = +s.slice(0, 4), m = +s.slice(4, 6);
    return new Date(Date.UTC(y, m - 1, 1));
  }
  const t = Date.parse(s);
  return Number.isFinite(t) ? new Date(t) : null;
}

/**
 * Number of whole days between `from` and `to` (signed). Positive when `to`
 * is in the future.
 * @param {Date | number} from
 * @param {Date | number} to
 * @returns {number}
 */
export function daysBetween(from, to) {
  const a = typeof from === 'number' ? from : from.getTime();
  const b = typeof to === 'number' ? to : to.getTime();
  return Math.round((b - a) / 86_400_000);
}

/**
 * Compute the colour-coded status for an expiry date.
 *   - already past:                red
 *   - within WARN_DAYS days:       orange
 *   - more than WARN_DAYS days:    green
 *
 * @param {Date | null} tokef
 * @returns {LicenseInfo}
 */
export function computeStatus(tokef) {
  if (!tokef || !Number.isFinite(tokef.getTime())) {
    return { status: 'unknown', tokefDate: null, daysLeft: null, prettyDate: null };
  }
  const daysLeft = daysBetween(Date.now(), tokef);
  /** @type {LicenseStatus} */
  let status;
  if (daysLeft < 0) status = 'red';
  else if (daysLeft <= WARN_DAYS) status = 'orange';
  else status = 'green';
  return {
    status,
    tokefDate: tokef,
    daysLeft,
    prettyDate: prettyDDMMYYYY(tokef),
  };
}

/**
 * Read the cached license expiry for a plate. Returns the parsed date, or
 * null when nothing is cached.
 *
 * @param {string} plate
 * @returns {Promise<Date | null>}
 */
export async function readTokefFromDB(plate) {
  const p = normalizePlate(plate);
  if (!p) return null;
  // The legacy schema stores it under `gov/vehicle/tokef_dt`.
  /** @type {{ vehicle?: { tokef_dt?: unknown } } | null} */
  const gov = await readOnce(`vehicles/${p}/gov`);
  const raw = gov?.vehicle?.tokef_dt;
  return parseTokef(raw);
}

/**
 * Convenience: full status for a plate. Looks up cached tokef, falls back
 * to refreshing the gov bundle if missing.
 *
 * @param {string} plate
 * @returns {Promise<LicenseInfo>}
 */
export async function getStatus(plate) {
  let tokef = await readTokefFromDB(plate);
  if (!tokef) {
    try {
      const bundle = await ensureGovBundle(plate);
      tokef = parseTokef(bundle.vehicle?.tokef_dt);
    } catch (e) {
      log.warn('getStatus: ensureGovBundle failed:', e);
    }
  }
  return computeStatus(tokef);
}

/**
 * Refresh tokef from gov data and persist if changed. Used by background
 * refresh jobs (legacy: `scheduleTokefAutoRefreshOnLoad`).
 *
 * @param {string} plate
 * @returns {Promise<{ refreshed: boolean; tokef: Date | null }>}
 */
export async function refreshFromGov(plate) {
  const p = normalizePlate(plate);
  if (!p) return { refreshed: false, tokef: null };
  try {
    const bundle = await ensureGovBundle(p, 0); // 0 maxAge → always refresh
    const tokef = parseTokef(bundle.vehicle?.tokef_dt);
    return { refreshed: !bundle.cached, tokef };
  } catch (err) {
    log.warn('refreshFromGov failed:', err);
    return { refreshed: false, tokef: null };
  }
}

/** @param {Date} d */
function prettyDDMMYYYY(d) {
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

// Suppress unused-import warning for items kept for future use.
void Ckan; void Auth; void fetchOwnershipHistory; void ref;
